"use client"

import { useMemo } from "react"
import { ArrowRight, Minus, Plus } from "lucide-react"

import { cn } from "@/common/lib/utils"
import { DEMO_CATALOG } from "@/modules/lernpfad/data/obligations"
import { composeCurriculum } from "@/modules/lernpfad/engine"
import type { Curriculum } from "@/modules/lernpfad/engine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"

/**
 * Before and after, for one person and for the whole organisation.
 *
 * This is the answer to "what would that actually do?", and the reason it can be
 * answered rather than asserted is that the engine is fast and pure: the
 * individual diff is a second `composeCurriculum` call costing well under a
 * millisecond, so it recomputes on every keystroke. The population figures come
 * from the same route the cockpit uses, against the DEBOUNCED policy — 1,960
 * people is ~25 ms, which is cheap but not free at slider frequency.
 *
 * Both sides compare against a baseline computed with NO policy at all, so
 * "unchanged" is a real statement about the organisation's standard settings rather than
 * about the last thing that was touched.
 */

function diffPlans(before: Curriculum, after: Curriculum) {
    const beforeById = new Map(before.items.map((item) => [item.baustein.id, item]))
    const afterById = new Map(after.items.map((item) => [item.baustein.id, item]))

    const added = after.items.filter((item) => !beforeById.has(item.baustein.id))
    const removed = before.items.filter((item) => !afterById.has(item.baustein.id))
    const moved = after.items.filter((item) => {
        const previous = beforeById.get(item.baustein.id)
        return previous !== undefined && previous.quarter !== item.quarter
    })
    return { added, removed, moved }
}

export default function ImpactPanel({ state }: { state: LernpfadDemoState }) {
    const {
        t,
        localizer,
        employee,
        constraints,
        baselineConstraints,
        policy,
    } = state

    const before = useMemo(
        () => composeCurriculum(employee, DEMO_CATALOG, baselineConstraints),
        [employee, baselineConstraints],
    )
    const after = useMemo(
        () => composeCurriculum(employee, DEMO_CATALOG, constraints),
        [employee, constraints],
    )
    const diff = useMemo(() => diffPlans(before, after), [before, after])

    const unchanged = policy === undefined

    return (
        <div className="space-y-4">
            <div className="lp-card rounded-xl border bg-card">
                <header className="border-b bg-muted/40 px-4 py-2.5">
                    <h3 className="text-sm font-medium">{t("steuer.impact.person")}</h3>
                    <p className="text-xs text-muted-foreground">{t("steuer.impact.subtitle")}</p>
                </header>

                <div className="space-y-3 p-4">
                    <p className="text-xs font-medium text-muted-foreground">
                        {employee.displayName}
                    </p>
                    <Delta
                        label={t("steuer.impact.items")}
                        before={before.items.length}
                        after={after.items.length}
                        localizer={localizer}
                    />
                    <Delta
                        label={t("steuer.impact.hours")}
                        before={before.totalHours}
                        after={after.totalHours}
                        localizer={localizer}
                    />
                    <Delta
                        label={t("steuer.impact.deferred")}
                        before={before.deferred.length}
                        after={after.deferred.length}
                        localizer={localizer}
                        lowerIsBetter
                    />

                    {(diff.added.length > 0 || diff.removed.length > 0 || diff.moved.length > 0) && (
                        <ul className="space-y-1 border-t pt-3">
                            {diff.added.map((item) => (
                                <ChangeRow
                                    key={`a-${item.baustein.id}`}
                                    kind="added"
                                    title={localizer.baustein(item.baustein).title}
                                    note={t("steuer.impact.added")}
                                />
                            ))}
                            {diff.removed.map((item) => (
                                <ChangeRow
                                    key={`r-${item.baustein.id}`}
                                    kind="removed"
                                    title={localizer.baustein(item.baustein).title}
                                    note={t("steuer.impact.removed")}
                                />
                            ))}
                            {diff.moved.map((item) => (
                                <ChangeRow
                                    key={`m-${item.baustein.id}`}
                                    kind="moved"
                                    title={localizer.baustein(item.baustein).title}
                                    note={t("steuer.impact.moved")}
                                />
                            ))}
                        </ul>
                    )}

                    {unchanged && (
                        <p className="border-t pt-3 text-xs text-muted-foreground">
                            {t("steuer.impact.unchanged")}
                        </p>
                    )}
                </div>

            </div>
        </div>
    )
}

function Delta({
    label,
    before,
    after,
    localizer,
    format,
    lowerIsBetter = false,
}: {
    label: string
    before?: number
    after?: number
    localizer: LernpfadDemoState["localizer"]
    format?: (value: number) => string
    lowerIsBetter?: boolean
}) {
    const render = (value: number) => (format ? format(value) : localizer.num(Math.round(value * 10) / 10))
    const changed = before !== undefined && after !== undefined && Math.abs(after - before) > 0.05
    // Direction is stated with an arrow and a word, never with colour alone —
    // and "better" depends on the metric, which is why it is a prop rather than
    // a sign test.
    const improved = changed && (lowerIsBetter ? after! < before! : after! > before!)

    return (
        <div className="flex items-baseline justify-between gap-3 text-xs">
            <span className="text-muted-foreground">{label}</span>
            <span className="flex shrink-0 items-baseline gap-1.5 tabular-nums">
                {before === undefined ? (
                    <span className="text-muted-foreground">—</span>
                ) : (
                    <span className={cn(changed && "text-muted-foreground line-through")}>
                        {render(before)}
                    </span>
                )}
                {changed && (
                    <>
                        <ArrowRight className="size-3 self-center text-muted-foreground" />
                        <span
                            className="font-semibold"
                            style={{ color: improved ? "var(--lp-good)" : "var(--lp-warning)" }}
                        >
                            {render(after!)}
                        </span>
                    </>
                )}
            </span>
        </div>
    )
}

function ChangeRow({
    kind,
    title,
    note,
}: {
    kind: "added" | "removed" | "moved"
    title: string
    note: string
}) {
    const Icon = kind === "added" ? Plus : kind === "removed" ? Minus : ArrowRight
    const colour =
        kind === "added" ? "var(--lp-good)" : kind === "removed" ? "var(--lp-critical)" : "var(--lp-ziel)"
    return (
        <li className="flex items-start gap-1.5 text-xs leading-snug">
            <Icon className="mt-0.5 size-3 shrink-0" style={{ color: colour }} />
            <span className="min-w-0 flex-1 truncate">{title}</span>
            <span className="shrink-0 text-muted-foreground">{note}</span>
        </li>
    )
}
